// Heatmap layer configuration.
//
// The Heatmap page renders one layer at a time, picked from this list
// via a tab/toggle. Adding a new layer = one entry here + bucket
// support on the backend. UI filters by `enabled: true`.
//
// `format` controls how the number is rendered in the popup + legend:
//   - "currency_per_m2"  → "6.428 €/m²"
//   - "currency"         → "234.500 €"
//   - "percent"          → "4,32 %"
//
// `colorScale` is consumed by `services/heatmap/colorScale.js`:
//   - "cool_warm"  → blue → yellow → red   (price)
//   - "warm_cool"  → red → orange → green  (yield — higher is better)
//   - "viridis"    → green → yellow → red  (rent)
//
export const LAYERS = [
  {
    id: "buy",
    label: "Kaufpreis €/m²",
    endpoint: "/heatmap/buy",
    colorScale: "cool_warm",
    format: "currency_per_m2",
    description: "Durchschnittlicher Kaufpreis pro m² je Bucket.",
    enabled: true,
  },
  {
    id: "rent",
    label: "Miete €/m²",
    endpoint: "/heatmap/rent",
    colorScale: "viridis",
    format: "currency_per_m2",
    description: "Durchschnittliche Kaltmiete pro m² je Bucket.",
    enabled: true,
  },
  {
    id: "yield",
    label: "Rendite %",
    endpoint: "/heatmap/yield",
    colorScale: "warm_cool",
    format: "percent",
    description: "Brutto-Mietrendite (12 × Miete/m² ÷ Kaufpreis/m²).",
    enabled: true,
  },
  // Future stubs — schema kept stable so flipping `enabled: true`
  // ships them without code refactor. Backends + bucket-types are
  // not yet wired; do NOT enable until that lands.
  {
    id: "deal_density",
    label: "Deal-Dichte",
    endpoint: "/heatmap/deal-density",
    colorScale: "viridis",
    format: "raw_count",
    description: "Anzahl aktiver Deals pro Bucket (zukünftig).",
    enabled: false,
  },
  {
    id: "ai_opportunity",
    label: "AI Opportunity Score",
    endpoint: "/heatmap/ai-opportunity",
    colorScale: "warm_cool",
    format: "score_100",
    description: "Aggregierter AI-Opportunity-Score je Bucket (zukünftig).",
    enabled: false,
  },
  {
    id: "price_trend",
    label: "Preisentwicklung",
    endpoint: "/heatmap/price-trend",
    colorScale: "cool_warm",
    format: "percent_delta",
    description: "Δ Kaufpreis/m² über 12 Monate (zukünftig).",
    enabled: false,
  },
];

export const DEFAULT_LAYER_ID = "buy";

export function getActiveLayers() {
  return LAYERS.filter((l) => l.enabled);
}

export function getLayerById(id) {
  return LAYERS.find((l) => l.id === id);
}

// Format a bucket value for popup + legend rendering.
export function formatValue(val, format) {
  if (val === null || val === undefined) return "—";
  switch (format) {
    case "currency_per_m2":
      return `${Math.round(val).toLocaleString("de-AT")} €/m²`;
    case "currency":
      return `${Math.round(val).toLocaleString("de-AT")} €`;
    case "percent":
      // Backend stores 0.0432 = 4.32% — multiply for display.
      return `${(val * 100).toFixed(2).replace(".", ",")} %`;
    case "percent_delta":
      return `${(val * 100).toFixed(1).replace(".", ",")} %`;
    case "raw_count":
      return `${Math.round(val).toLocaleString("de-AT")}`;
    case "score_100":
      return `${Math.round(val)} / 100`;
    default:
      return String(val);
  }
}

// Bucket → colour mapping. Domain is computed once per fetch so the
// scale matches the visible data, not a fixed global.
export function colourFor(value, domainMin, domainMax, scale) {
  if (value === null || value === undefined) return "#52525b";
  const span = domainMax - domainMin || 1;
  const t = Math.max(0, Math.min(1, (value - domainMin) / span));
  // Linear interpolation between 3 stops.
  const stops = SCALE_STOPS[scale] || SCALE_STOPS.viridis;
  if (t <= 0.5) {
    return interpolateColor(stops[0], stops[1], t * 2);
  }
  return interpolateColor(stops[1], stops[2], (t - 0.5) * 2);
}

const SCALE_STOPS = {
  cool_warm: ["#2563eb", "#facc15", "#dc2626"], // blue → yellow → red
  warm_cool: ["#dc2626", "#facc15", "#16a34a"], // red → yellow → green
  viridis: ["#0ea5e9", "#facc15", "#ea580c"],   // teal → yellow → orange
};

function interpolateColor(c1, c2, t) {
  const a = hexToRgb(c1);
  const b = hexToRgb(c2);
  const r = Math.round(a[0] + (b[0] - a[0]) * t);
  const g = Math.round(a[1] + (b[1] - a[1]) * t);
  const bl = Math.round(a[2] + (b[2] - a[2]) * t);
  return `rgb(${r}, ${g}, ${bl})`;
}

function hexToRgb(hex) {
  const m = hex.replace("#", "");
  return [
    parseInt(m.slice(0, 2), 16),
    parseInt(m.slice(2, 4), 16),
    parseInt(m.slice(4, 6), 16),
  ];
}
